import { formatMeasure } from './measure';

export interface IStatusTemplates {
    payloadMet: string | null;
    reachMet: string | null;
    bothShort: string | null;
    locale: string;
}

export interface IShortfall {
    payloadShortfallKg: number;
    reachShortfallMm: number;
}

export interface IRequirementFigures {
    payloadKg: number;
    reachMm: number;
}

type TemplateToken = 'payload' | 'reach' | 'matches' | 'count' | 'selection';

/** Replaces every `{token}` in a template. Tokens without a value are left as written. */
export function fillTemplate(template: string, values: Partial<Record<TemplateToken, string>>): string {
    let filled = template;

    for (const [token, value] of Object.entries(values)) {
        if (value !== undefined) {
            filled = filled.replaceAll(`{${token}}`, value);
        }
    }

    return filled;
}

export function measures(figures: IRequirementFigures, locale: string): Pick<Record<TemplateToken, string>, 'payload' | 'reach'> {
    return {
        payload: formatMeasure(locale, 'kilogram', figures.payloadKg),
        reach: formatMeasure(locale, 'millimeter', figures.reachMm),
    };
}

export function statusLineFor(
    shortfall: IShortfall,
    requirement: IRequirementFigures,
    templates: IStatusTemplates,
): string {
    const payloadMet = shortfall.payloadShortfallKg === 0;
    const reachMet = shortfall.reachShortfallMm === 0;

    const template = payloadMet ? templates.payloadMet : reachMet ? templates.reachMet : templates.bothShort;

    if (!template) {
        return '';
    }

    return fillTemplate(
        template,
        measures(
            {
                payloadKg: payloadMet ? requirement.payloadKg : shortfall.payloadShortfallKg,
                reachMm: reachMet ? requirement.reachMm : shortfall.reachShortfallMm,
            },
            templates.locale,
        ),
    );
}

export function fillNoMatchSubtext(
    subtext: string | null,
    requirement: IRequirementFigures,
    templates: IStatusTemplates,
): string {
    return subtext ? fillTemplate(subtext, measures(requirement, templates.locale)) : '';
}

export function fillRankedSubheading(
    subheading: string | null,
    figures: { matches: number; portfolioSize: number },
    requirement: IRequirementFigures,
    templates: IStatusTemplates,
): string {
    if (!subheading) {
        return '';
    }

    return fillTemplate(subheading, {
        ...measures(requirement, templates.locale),
        matches: String(figures.matches),
        count: String(figures.portfolioSize),
    });
}
